/**
 * UserActions — admin status/role controls for one user.
 *
 * Backs onto PATCH /api/admin/users/:id/status and .../role. Both endpoints
 * refuse to act on the caller's own account (403), so the whole control set is
 * hidden when an admin is looking at themselves.
 *
 * Reason rules mirror the server's validator exactly: suspend and ban require
 * 3-1000 chars, deactivate takes an optional note, reactivate takes none.
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  MenuItem,
  Alert,
  CircularProgress,
  Typography,
  Divider,
} from '@mui/material';
import {
  Block,
  PauseCircleOutline,
  PowerSettingsNew,
  CheckCircleOutline,
  ManageAccounts,
} from '@mui/icons-material';

import adminService from '../../services/adminService';
import { status as statusTokens, text, border } from '../../theme/colors';
import { formatCurrency } from '../../utils/helpers';

// Every role users_role_check permits.
const ROLES = [
  'client',
  'contractor',
  'verifier',
  'quality_inspector',
  'project_manager',
  'merchant',
  'realtor',
  'conveyancer',
  'moderator',
  'admin',
];

const REASON_MIN = 3;
const REASON_MAX = 1000;

// Keyed by the target status. `reason: 'required'|'optional'|'none'` matches
// the server-side validator.
const STATUS_ACTIONS = {
  active: {
    label: 'Reactivate',
    icon: <CheckCircleOutline />,
    colour: statusTokens.success,
    reason: 'none',
    title: 'Reactivate this account?',
    body: 'The user regains access immediately and is sent a reactivation notice.',
  },
  suspended: {
    label: 'Suspend',
    icon: <PauseCircleOutline />,
    colour: statusTokens.warning,
    reason: 'required',
    allowUntil: true,
    title: 'Suspend this account?',
    body: 'The user cannot sign in while suspended. They are emailed the reason. Leave the end date empty for an indefinite suspension.',
  },
  deactivated: {
    label: 'Deactivate',
    icon: <PowerSettingsNew />,
    colour: statusTokens.neutral,
    reason: 'optional',
    title: 'Deactivate this account?',
    body: 'The account is switched off and the user cannot sign in. This is reversible — no email is sent.',
  },
  banned: {
    label: 'Ban',
    icon: <Block />,
    colour: statusTokens.error,
    reason: 'required',
    destructive: true,
    title: 'Ban this account?',
    body: 'A ban is terminal. The user is emailed the reason, and any withdrawable balance is automatically queued as a final payout.',
  },
};

const UserActions = ({ user, currentUserId, onChanged }) => {
  // 'status:<target>' | 'role' | null
  const [dialog, setDialog] = useState(null);
  const [reason, setReason] = useState('');
  const [until, setUntil] = useState('');
  const [nextRole, setNextRole] = useState(user?.role || '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  // Only meaningful for suspend/ban, so it loads with those dialogs.
  const [balance, setBalance] = useState(null);
  const [balanceLoading, setBalanceLoading] = useState(false);

  /**
   * Suspending or banning an earning role has money consequences: on a ban
   * AccountStatusService auto-creates a final payout for whatever is
   * withdrawable. The legacy dialog surfaced that figure and this one must
   * too, or an admin bans blind.
   *
   * Declared above the isSelf early-return — a hook after it would change the
   * hook count on the render where isSelf flips.
   */
  useEffect(() => {
    if (dialog !== 'status:suspended' && dialog !== 'status:banned') {
      setBalance(null);
      return undefined;
    }
    let cancelled = false;
    setBalanceLoading(true);
    adminService.getUserBalance(user.id)
      .then((data) => { if (!cancelled) setBalance(data || null); })
      // Non-fatal: `false` renders as "couldn't be loaded" rather than blocking.
      .catch(() => { if (!cancelled) setBalance(false); })
      .finally(() => { if (!cancelled) setBalanceLoading(false); });
    return () => { cancelled = true; };
  }, [dialog, user?.id]);

  const isSelf = !!currentUserId && currentUserId === user?.id;
  if (isSelf) {
    return (
      <Alert severity="info" sx={{ borderRadius: 2 }}>
        This is your own account. Status and role changes are blocked to stop you locking yourself out.
      </Alert>
    );
  }

  const open = (key) => {
    setError(null);
    setReason('');
    setUntil('');
    setNextRole(user.role);
    setDialog(key);
  };

  const close = () => {
    if (busy) return;
    setDialog(null);
    setError(null);
  };

  const targetStatus = dialog?.startsWith('status:') ? dialog.slice(7) : null;
  const action = targetStatus ? STATUS_ACTIONS[targetStatus] : null;

  const reasonInvalid =
    action?.reason === 'required' &&
    (reason.trim().length < REASON_MIN || reason.trim().length > REASON_MAX);

  const untilInvalid = !!until && new Date(until) <= new Date();

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      if (targetStatus) {
        await adminService.setUserStatus(user.id, targetStatus, {
          ...(action.reason !== 'none' && reason.trim() ? { reason: reason.trim() } : {}),
          ...(action.allowUntil && until ? { suspendedUntil: new Date(until).toISOString() } : {}),
        });
      } else {
        await adminService.setUserRole(user.id, nextRole);
      }
      setDialog(null);
      onChanged?.();
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'The change could not be saved.');
    } finally {
      setBusy(false);
    }
  };

  // Never offer the status the account is already in.
  const available = Object.keys(STATUS_ACTIONS).filter((key) => key !== user?.status);

  return (
    <>
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
        {available.map((key) => {
          const a = STATUS_ACTIONS[key];
          return (
            <Button
              key={key}
              size="small"
              variant={key === 'active' ? 'contained' : 'outlined'}
              disableElevation
              startIcon={a.icon}
              onClick={() => open(`status:${key}`)}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                ...(key === 'active'
                  ? { bgcolor: a.colour, '&:hover': { bgcolor: statusTokens.successDark } }
                  : { color: a.colour, borderColor: `${a.colour}66`, '&:hover': { borderColor: a.colour } }),
              }}
            >
              {a.label}
            </Button>
          );
        })}

        <Button
          size="small"
          variant="outlined"
          startIcon={<ManageAccounts />}
          onClick={() => open('role')}
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            color: text.secondary,
            borderColor: `${text.tertiary}66`,
            '&:hover': { borderColor: text.secondary },
          }}
        >
          Change role
        </Button>
      </Box>

      {/* Status change */}
      <Dialog open={!!targetStatus} onClose={close} fullWidth maxWidth="sm">
        {action && (
          <>
            <DialogTitle sx={{ fontWeight: 700 }}>{action.title}</DialogTitle>
            <DialogContent>
              <DialogContentText sx={{ mb: 2 }}>{action.body}</DialogContentText>

              <Typography variant="body2" sx={{ color: text.secondary, mb: 2 }}>
                {user.email} · currently <strong>{user.status}</strong>
              </Typography>

              {/* Money consequences — GET /admin/users/:id/balance */}
              {balanceLoading ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                  <CircularProgress size={16} />
                  <Typography variant="body2" sx={{ color: text.secondary }}>
                    Checking balance…
                  </Typography>
                </Box>
              ) : balance === false ? (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  Could not load this user's balance — proceed knowing a ban may still
                  trigger an automatic final payout.
                </Alert>
              ) : balance && balance.recipientType ? (
                <Box
                  sx={{
                    mb: 2,
                    p: 1.75,
                    borderRadius: 2,
                    border: `1px solid ${border.light}`,
                    bgcolor: Number(balance.withdrawable_balance) > 0
                      ? statusTokens.warningBg
                      : 'transparent',
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{ color: text.tertiary, fontWeight: 600, letterSpacing: 0.3, textTransform: 'uppercase' }}
                  >
                    Payout position ({String(balance.recipientType).replace(/_/g, ' ')})
                  </Typography>

                  <Typography sx={{ fontSize: 20, fontWeight: 700, color: text.primary, mt: 0.25 }}>
                    {formatCurrency(Number(balance.withdrawable_balance) || 0)} withdrawable
                  </Typography>

                  <Divider sx={{ my: 1.25 }} />

                  <Box sx={{ display: 'flex', gap: 2.5, flexWrap: 'wrap' }}>
                    {[
                      ['Earned', balance.total_earnings],
                      ['In flight', balance.in_flight_withdrawals],
                      ['Paid out', balance.completed_withdrawals],
                    ].map(([label, value]) => (
                      <Box key={label}>
                        <Typography variant="caption" sx={{ color: text.tertiary, display: 'block' }}>
                          {label}
                        </Typography>
                        <Typography variant="body2" sx={{ color: text.primary, fontWeight: 600 }}>
                          {formatCurrency(Number(value) || 0)}
                        </Typography>
                      </Box>
                    ))}
                  </Box>

                  {targetStatus === 'banned' && Number(balance.withdrawable_balance) > 0 && (
                    <Typography
                      variant="caption"
                      sx={{ color: statusTokens.warningDark, fontWeight: 600, display: 'block', mt: 1.25 }}
                    >
                      Banning will automatically queue a final payout of{' '}
                      {formatCurrency(Number(balance.withdrawable_balance))}.
                    </Typography>
                  )}
                </Box>
              ) : balance ? (
                // Clients/admins/merchants don't earn into payment_recipients.
                <Typography variant="caption" sx={{ color: text.tertiary, display: 'block', mb: 2 }}>
                  This role holds no payout balance.
                </Typography>
              ) : null}

              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              {action.reason !== 'none' && (
                <TextField
                  fullWidth
                  multiline
                  minRows={3}
                  label={action.reason === 'required' ? 'Reason (required)' : 'Reason (optional)'}
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  error={!!reason && reasonInvalid}
                  helperText={
                    action.reason === 'required'
                      ? `${REASON_MIN}-${REASON_MAX} characters. The user sees this in their email.`
                      : 'Kept on the account record. Not emailed.'
                  }
                  sx={{ mb: 2 }}
                />
              )}

              {action.allowUntil && (
                <TextField
                  fullWidth
                  type="datetime-local"
                  label="Suspend until (optional)"
                  value={until}
                  onChange={(e) => setUntil(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  error={untilInvalid}
                  helperText={
                    untilInvalid
                      ? 'Must be in the future.'
                      : 'Leave empty to suspend indefinitely.'
                  }
                />
              )}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
              <Button onClick={close} disabled={busy} sx={{ textTransform: 'none' }}>
                Cancel
              </Button>
              <Button
                variant="contained"
                disableElevation
                onClick={submit}
                disabled={busy || reasonInvalid || untilInvalid}
                startIcon={busy ? <CircularProgress size={16} color="inherit" /> : action.icon}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  bgcolor: action.colour,
                  '&:hover': { bgcolor: action.colour, filter: 'brightness(0.92)' },
                }}
              >
                {action.destructive ? `Yes, ${action.label.toLowerCase()}` : action.label}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Role change */}
      <Dialog open={dialog === 'role'} onClose={close} fullWidth maxWidth="xs">
        <DialogTitle sx={{ fontWeight: 700 }}>Change role</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: text.secondary, mb: 2 }}>
            {user.email} · currently <strong>{(user.role || '').replace(/_/g, ' ')}</strong>
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <TextField
            select
            fullWidth
            label="New role"
            value={nextRole}
            onChange={(e) => setNextRole(e.target.value)}
            helperText="Changing a role changes which dashboard and permissions the user gets."
          >
            {ROLES.map((r) => (
              <MenuItem key={r} value={r} sx={{ textTransform: 'capitalize' }}>
                {r.replace(/_/g, ' ')}
              </MenuItem>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={close} disabled={busy} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disableElevation
            onClick={submit}
            disabled={busy || !nextRole || nextRole === user.role}
            startIcon={busy ? <CircularProgress size={16} color="inherit" /> : <ManageAccounts />}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            Save role
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default UserActions;
